import {
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import React from 'react'

import {
  apartmentEnrolmentOptions,
  apartmentParentSearch,
  billingEndDate,
  computeEnrolmentFee,
  kitTotalFor,
  createApartmentPaymentLink,
  fetchApartmentEnrolments,
  fetchApartmentPricing
} from 'api/apartment'
import { AutoComplete } from 'app/components/generic/FormFields'
import { Loading } from 'app/components/generic/LottieComponents'
import Table from 'app/components/generic/Table'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'
import { formatAmount } from 'app/utils'

import ApartmentPageHeader from '../shared/ApartmentPageHeader'
import { useSelectedApartment } from '../shared/apartmentStore'
import { PaymentLinkCell, PaymentLinkPanel } from '../shared/PaymentLinkCopy'

const useStyles = makeStyles((theme) => ({
  // Edge-to-edge table (own horizontal scroll); only the header is padded.
  root: { minWidth: 0, overflowX: 'hidden' },
  header: { padding: theme.spacing(3, 3, 0) },
  studentField: { minWidth: 280, marginBottom: theme.spacing(3) },
  category: { marginBottom: theme.spacing(2.5) },
  categoryName: { fontWeight: 600 },
  hint: { color: theme.palette.text.secondary, fontSize: 12 },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  cost: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6
  },
  costHeading: { fontWeight: 600, marginBottom: theme.spacing(1) },
  costRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0)
  },
  costLabel: { color: theme.palette.text.secondary },
  costTotal: {
    fontWeight: 700,
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1)
  },
  cta: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3)
  },
  dateRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  }
}))

// Mirrors the main system's Excel enrolment list, keeping the columns an
// apartment actually has. Dropped from that list: Centre, Program Type, Payment
// Mode, Is discovery?, and the whole termination cluster (Termination
// Requested/On/By/Reason/Notes/Source) plus adjusted_end_date — ApartmentEnrolment
// has no fields behind any of them.
const buildColumns = (onEnrolAgain, rows) => [
  { name: 'childName', label: 'Student Name' },
  { name: 'grade', label: 'Student Grade' },
  { name: 'parentName', label: 'Parent Name' },
  { name: 'phone', label: 'Parent Number', options: { filter: false } },
  { name: 'age', label: 'Age', options: { filter: false } },
  { name: 'teacherName', label: 'Teacher Name' },
  { name: 'program', label: 'Course Subject' },
  { name: 'level', label: 'Batch Level' },
  { name: 'batch', label: 'Batch' },
  { name: 'days', label: 'Days', options: { filter: false } },
  { name: 'time', label: 'Batch Timings', options: { filter: false } },
  { name: 'frequency', label: 'Freq' },
  { name: 'startDate', label: 'Start Date', options: { filter: false } },
  { name: 'endDate', label: 'End Date', options: { filter: false } },
  { name: 'status', label: 'Status' },
  { name: 'createdOn', label: 'Booked On', options: { filter: false } },
  {
    // The newest Razorpay link on this enrolment, whatever its status, so the
    // team can still recover it when the booking-time copy was missed. Rows
    // show a dash when the link predates Razorpay (sheet imports carry a
    // payment id and no URL) — those are settled offline and need no resend.
    name: 'paymentUrl',
    label: 'Payment Link',
    options: {
      filter: false,
      sort: false,
      customBodyRenderLite: (dataIndex) => {
        const row = rows?.[dataIndex]
        return (
          <PaymentLinkCell url={row?.paymentUrl} status={row?.paymentStatus} />
        )
      }
    }
  },
  {
    name: 'parentId',
    label: 'Actions',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (parentId) => (
        <Button
          size="small"
          color="primary"
          onClick={() => onEnrolAgain(parentId)}
        >
          Enrol again
        </Button>
      )
    }
  }
]

const tableOptions = {
  selectableRows: 'none',
  filterType: 'dropdown',
  // Match the main system: the grid is end-to-end with ONE horizontal
  // scroller of its own and a fixed header. Do not wrap it in another
  // overflow container or you get two scrollbars.
  responsive: 'vertical',
  fixedHeader: true,
  tableBodyMaxHeight: 'calc(100vh - 320px)',
  print: false,
  download: true,
  viewColumns: false,
  rowsPerPage: 25,
  rowsPerPageOptions: [10, 15, 25, 100],
  elevation: 0
}

const programPrice = (pricing, program, count) => {
  const row = (pricing?.programs || []).find((p) => p.program === program)
  if (!row) return 0
  return count >= 2 ? row.twiceWeekly : row.onceWeekly
}

const todayISO = () => new Date().toISOString().slice(0, 10)

// Why the bill is not one clean month, in the coordinator's words. Two separate
// reasons and the line has to carry both: the WEEK of the start date sets the
// share of the join month (1-7 all, 8-14 three quarters, 15-21 half, 22-end a
// quarter), and joining from the 15th ALSO buys next month.
// Mirrors `prorataFactors` in api/apartment.js — if the factors change, so do
// these words.
const MONTH_SHARE = {
  1: 'All',
  0.75: 'Three quarters',
  0.5: 'Half',
  0.25: 'A quarter'
}
const prorataLabel = ({ currentMonthFactor, nextMonthFactor }) =>
  `${
    MONTH_SHARE[currentMonthFactor] || `×${currentMonthFactor}`
  } of this month${nextMonthFactor > 0 ? ' + all of next' : ''}`

// A slot's label is its identity in the UI; the backend wants its batch uuid.
const labelForBatchId = (options, program, batchId) =>
  (options.batchesByProgram[program] || []).find((s) => s.batchId === batchId)
    ?.label

// …and the reverse: the full slot behind a label, which carries the batch uuid
// and the level the kit charge keys on.
const labelSlot = (options, program, label) =>
  (options.batchesByProgram[program] || []).find((s) => s.label === label)

// The slots a given child may be booked into: those whose batch the backend
// says their grade (or, failing that, their age) matches. A batch they are
// already enrolled in always shows, whatever the rule now says — otherwise the
// coordinator could not see, or remove, an existing enrolment.
const eligibleSlots = (options, child, program) => {
  const allowed = new Set(child.eligibleBatchIds || [])
  const enrolled = new Set((child.existing || []).map((e) => e.batchId))
  return (options.batchesByProgram[program] || []).filter(
    (slot) => allowed.has(slot.batchId) || enrolled.has(slot.batchId)
  )
}

// Does an `existing` row hold its seat on `day`? Mirrors `seat_window_q` on the
// backend: inside the row's own date range, NULL end meaning open-ended. A row
// with no dates at all is treated as held — old rows predate the dates being
// sent, and dropping them would silently under-count a real basket.
const holdsSeatOn = (e, day) => {
  if (!day) return true
  if (!e.startDate && !e.endDate) return true
  if (e.startDate && e.startDate > day) return false
  if (e.endDate && e.endDate < day) return false
  return true
}

// The batches a child is already in, as {program: [label, ...]}. This is what
// gets pre-ticked so the coordinator adds and subtracts rather than re-entering.
const existingSlots = (options, child) => {
  const out = {}
  ;(child.existing || []).forEach((e) => {
    const label = labelForBatchId(options, e.program, e.batchId)
    if (!label) return
    out[e.program] = [...(out[e.program] || []), label]
  })
  return out
}

// Pick a parent, tick their children, then 1–2 day/time slots per category per
// child. One bill for the whole family — the discount tier counts every child's
// selections together, which is why the backend takes a parent, not a child.
// Cost is a preview; the backend computes the authoritative fee on create.
const NewBookingForm = ({
  classes,
  apartmentId,
  options,
  pricing,
  submitting,
  onSubmit,
  initialParentId
}) => {
  const [parentId, setParentId] = React.useState('')
  // The selected parent object carries its children. A searched (non-apartment)
  // parent is NOT in options.parents, so we keep the picked object itself rather
  // than looking it up in the loaded list.
  const [picked, setPicked] = React.useState(null)
  // { [childId]: { [program]: [label, ...] } }
  const [picks, setPicks] = React.useState({})
  const [chosen, setChosen] = React.useState([])
  const [startDate, setStartDate] = React.useState(todayISO())
  // Not chosen — a consequence of the start date and the prorata rule.
  const endDate = billingEndDate(startDate)

  const parent = picked

  // Live search across ALL main-system parents by name/phone — not just this
  // apartment's families. The backend returns each parent's children shaped like
  // the options payload (eligibility already resolved for this apartment), so a
  // searched parent books exactly like a loaded one; create_booking attaches
  // them to this building on submit.
  const searchParents = React.useCallback(
    async (text) => {
      const res = await apartmentParentSearch(apartmentId, text)
      return res.map((p) => ({
        ...p,
        uuid: p.id,
        name: `${p.name} · ${p.phone}`
      }))
    },
    [apartmentId]
  )

  // Selecting a parent pre-selects every child and their current batches.
  const selectParent = React.useCallback(
    (opt) => {
      if (!opt) {
        setParentId('')
        setPicked(null)
        setChosen([])
        setPicks({})
        return
      }
      setParentId(opt.id)
      setPicked(opt)
      setChosen(opt.children.map((c) => c.id))
      const next = {}
      opt.children.forEach((c) => {
        next[c.id] = existingSlots(options, c)
      })
      setPicks(next)
    },
    [options]
  )

  // Pre-select the family for the "enrol again" flow — an existing apartment
  // parent taken from the loaded options.
  const initialParentOption = React.useMemo(() => {
    const p = options.parents.find((x) => x.id === initialParentId)
    return p ? { ...p, uuid: p.id, name: `${p.name} · ${p.phone}` } : null
  }, [options.parents, initialParentId])

  React.useEffect(() => {
    if (initialParentOption) selectParent(initialParentOption)
  }, [initialParentOption, selectParent])

  const toggleChild = (childId) =>
    setChosen((prev) =>
      prev.includes(childId)
        ? prev.filter((c) => c !== childId)
        : [...prev, childId]
    )

  const toggleSlot = (childId, program, label) =>
    setPicks((prev) => {
      const forChild = prev[childId] || {}
      const current = forChild[program] || []
      let next
      if (current.includes(label)) next = current.filter((l) => l !== label)
      else if (current.length >= 2) return prev
      // max two timings per category
      else next = [...current, label]
      const copyChild = { ...forChild }
      if (next.length === 0) delete copyChild[program]
      else copyChild[program] = next
      return { ...prev, [childId]: copyChild }
    })

  // Every selected child's lines, flattened. The family discount is computed
  // over all of them at once.
  const lines = chosen.flatMap((childId) =>
    Object.entries(picks[childId] || {}).map(([program, labels]) => ({
      childId,
      program,
      count: labels.length,
      frequency: labels.length >= 2 ? '2x' : '1x',
      amount: programPrice(pricing, program, labels.length)
    }))
  )

  // Every selected batch, resolved to the (child, subject, level) triple the
  // kit rule keys on.
  const selectedBatches = chosen.flatMap((childId) =>
    Object.entries(picks[childId] || {}).flatMap(([program, labels]) =>
      labels
        .map((label) => labelSlot(options, program, label))
        .filter(Boolean)
        .map((slot) => ({ childId, program, level: slot.level }))
    )
  )
  // Kits each child has already paid for. Both sources matter: `options.students`
  // covers the families already enrolled in THIS building, and the picked
  // parent's own children cover a SEARCHED parent — who is not in options at
  // all (see the note on `picked` above).
  //
  // Reading only options.students re-charged the kit on every booking made via
  // search: the lookup missed, `kitTotalFor` saw no payment, and a child who
  // already owned the kit was billed for it again. The parent object is second
  // so it wins, being the payload the booking is actually being built from.
  const kitsPaidByChild = {
    ...(options.students || []).reduce((acc, s) => {
      acc[s.id] = s.kitsPaid || []
      return acc
    }, {}),
    ...(parent?.children || []).reduce((acc, c) => {
      acc[c.id] = c.kitsPaid || []
      return acc
    }, {})
  }
  const kitTotal = kitTotalFor(selectedBatches, kitsPaidByChild, pricing)

  // Every seat the family already holds, across ALL the parent's children,
  // because the backend's discount tier counts exactly that
  // (`family_seats_on(parent, start_date)`), one entry per enrolment row —
  // EXCEPT a seat ticked on this booking. Those are pre-ticked for a renewal and
  // are already charged in `lines`; counting them as held too doubled the basket.
  //
  // And except a seat that is not live on the start date. An apartment enrolment
  // is ONE ROW PER MONTH and nothing sweeps a finished one, so `existing` also
  // carries every month the family has ever bought. Counting those pushed the
  // family up the discount ladder on seats they no longer hold. Mirror of the
  // backend window, and it has to stay a mirror or the preview quotes a number
  // the parent is not charged.
  const bookedKeys = new Set(
    chosen.flatMap((childId) =>
      Object.entries(picks[childId] || {}).flatMap(([program, labels]) =>
        labels
          .map((label) => labelSlot(options, program, label)?.batchId)
          .filter(Boolean)
          .map((batchId) => `${childId}|${batchId}`)
      )
    )
  )
  const heldSelections = (parent?.children || []).flatMap((c) =>
    (c.existing || [])
      .filter((e) => !bookedKeys.has(`${c.id}|${e.batchId}`))
      .filter((e) => holdsSeatOn(e, startDate))
      .map((e) => ({ program: e.program, frequency: e.frequency }))
  )

  // The backend prorates off `start_date`, adds the same kits and discounts on
  // the family's whole basket, so the preview must use all three or the parent
  // is told one number and charged another.
  const fee = computeEnrolmentFee(
    lines.map((l) => ({ program: l.program, frequency: l.frequency })),
    pricing,
    startDate,
    kitTotal,
    heldSelections
  )
  const canSubmit = Boolean(parentId) && lines.length > 0 && !submitting

  const submit = () => {
    const children = chosen
      .map((childId) => ({
        child: childId,
        selections: Object.entries(picks[childId] || {}).map(
          ([program, labels]) => ({
            program,
            slots: labels
              .map((label) => labelSlot(options, program, label))
              .filter(Boolean)
              .map((slot) => ({ batch: slot.batchId }))
          })
        )
      }))
      .filter((c) => c.selections.length > 0)
    onSubmit({ parent: parentId, children, startDate })
  }

  return (
    <>
      <FormControl variant="outlined" className={classes.studentField}>
        {/* Live search over ALL main-system parents by name or number — the
            backend returns each parent's children (eligibility already resolved
            for this apartment), so a centre or brand-new family can be booked in
            and gets attached to the building on submit. */}
        <AutoComplete
          // Apartment-scoped opt-ins: 300ms throttle instead of the shared
          // 5000ms default (five seconds of an empty box reads as broken),
          // and fetch-on-open so clicking shows options instead of an empty
          // menu. Both default to the OLD behaviour everywhere else in Alfred.
          throttleMs={300}
          fetchOnOpen
          async
          name="bookingParent"
          label="Parent (search by name or number)"
          multiple={false}
          fetchValues={searchParents}
          initialValue={initialParentOption}
          onChange={(e, opt) => selectParent(opt)}
          size="small"
        />
      </FormControl>

      <div className={classes.dateRow}>
        <TextField
          label="Start date"
          type="date"
          size="small"
          variant="outlined"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Paid up to (auto)"
          type="date"
          size="small"
          variant="outlined"
          value={endDate || ''}
          InputLabelProps={{ shrink: true }}
          InputProps={{ readOnly: true }}
          helperText="Derived from the start date"
        />
      </div>

      {parent &&
        parent.children.map((child) => {
          const included = chosen.includes(child.id)
          return (
            <div className={classes.category} key={child.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={included}
                    onChange={() => toggleChild(child.id)}
                  />
                }
                label={<strong>{child.name}</strong>}
              />
              {included &&
                options.programs.map((program) => {
                  const selected = (picks[child.id] || {})[program] || []
                  const already = existingSlots(options, child)[program] || []
                  const slots = eligibleSlots(options, child, program)
                  // A subject with nothing this child is old enough for is not
                  // an empty section — it is not offered to them at all.
                  if (slots.length === 0) return null
                  return (
                    <div className={classes.category} key={program}>
                      <Typography className={classes.categoryName}>
                        {program}
                      </Typography>
                      <span className={classes.hint}>
                        Select one or two timings
                      </span>
                      <div className={classes.chipRow}>
                        {slots.map((slot) => {
                          const isSelected = selected.includes(slot.label)
                          const isExisting = already.includes(slot.label)
                          // `null` capacity means uncapped, so no seat count.
                          const seats =
                            slot.seatsAvailable === null ||
                            slot.seatsAvailable === undefined
                              ? ''
                              : ` · ${slot.seatsAvailable} left`
                          return (
                            <Chip
                              key={slot.label}
                              // Mark what they are already enrolled in, so
                              // removing a chip is a visible subtraction.
                              label={
                                isExisting
                                  ? `${slot.label} · enrolled`
                                  : `${slot.label}${seats}`
                              }
                              clickable
                              color={isSelected ? 'primary' : 'default'}
                              variant={isSelected ? 'default' : 'outlined'}
                              onClick={() =>
                                toggleSlot(child.id, program, slot.label)
                              }
                            />
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              {included &&
                (() => {
                  // Two unrelated failures used to read identically here. A
                  // building whose batches have no Days recorded yields zero
                  // slots for everyone, and blaming the child's grade for that
                  // sends the coordinator hunting a problem that isn't there.
                  // Separate them: no slots anywhere is a batch-setup problem,
                  // slots that exist but don't fit is a grade/age one.
                  const anyEligible = options.programs.some(
                    (program) =>
                      eligibleSlots(options, child, program).length > 0
                  )
                  if (anyEligible) return null
                  const anySlots = options.programs.some(
                    (program) =>
                      (options.batchesByProgram[program] || []).length > 0
                  )
                  return (
                    <span className={classes.hint}>
                      {anySlots
                        ? `No batches match ${child.name}'s grade or age.`
                        : 'No batch here has its Days set, so there are no ' +
                          'timings to book. Set Days on each batch under ' +
                          'Batches, then try again.'}
                    </span>
                  )
                })()}
            </div>
          )
        })}

      <div className={classes.cost}>
        <Typography className={classes.costHeading}>Cost</Typography>
        {lines.length === 0 ? (
          <Typography variant="body2" className={classes.costLabel}>
            Pick a parent and select timings to see the cost.
          </Typography>
        ) : (
          <>
            {lines.map((l) => (
              <div
                className={classes.costRow}
                key={`${l.childId}-${l.program}`}
              >
                <span className={classes.costLabel}>
                  {l.program} · {l.count} session{l.count > 1 ? 's' : ''}/wk
                </span>
                <span>{formatAmount(l.amount)}</span>
              </div>
            ))}
            {fee.monthsBilled !== 1 && (
              <div className={classes.costRow}>
                <span className={classes.costLabel}>{prorataLabel(fee)}</span>
                <span>×{fee.monthsBilled}</span>
              </div>
            )}
            {fee.discount > 0 && (
              <div className={classes.costRow}>
                <span className={classes.costLabel}>
                  Family discount ({fee.discountPercent}%
                  {fee.heldCount > 0 &&
                    `, counts ${fee.heldCount} class${
                      fee.heldCount > 1 ? 'es' : ''
                    } already held`}
                  )
                </span>
                <span>−{formatAmount(fee.discount)}</span>
              </div>
            )}
            {kitTotal > 0 && (
              <div className={classes.costRow}>
                <span className={classes.costLabel}>
                  Kit (one-off, once per level)
                </span>
                <span>{formatAmount(kitTotal)}</span>
              </div>
            )}
            <Divider />
            <div className={`${classes.costRow} ${classes.costTotal}`}>
              <span>Total (GST 18% incl.)</span>
              <span>{formatAmount(fee.total)}</span>
            </div>
          </>
        )}
      </div>

      <div className={classes.cta}>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          onClick={submit}
        >
          {submitting
            ? 'Creating…'
            : 'Create one Razorpay link & send on WhatsApp'}
        </Button>
      </div>
    </>
  )
}

const ApartmentEnrolments = () => {
  const classes = useStyles()
  const [apartment] = useSelectedApartment()
  const [rows, setRows] = React.useState(null)
  const [options, setOptions] = React.useState(null)
  const [pricing, setPricing] = React.useState(null)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  // "Enrol again" opens the same modal with this parent (and therefore their
  // children and current batches) already filled in.
  const [prefillParentId, setPrefillParentId] = React.useState('')
  // The link just minted, kept until the coordinator dismisses it so it cannot
  // be lost the way a toast is. Cleared whenever the booking modal reopens.
  const [createdLink, setCreatedLink] = React.useState(null)

  React.useEffect(() => {
    let active = true
    setRows(null)
    setOptions(null)
    setPricing(null)
    fetchApartmentEnrolments(apartment.id).then((res) => active && setRows(res))
    apartmentEnrolmentOptions(apartment.id).then(
      (res) => active && setOptions(res)
    )
    fetchApartmentPricing(apartment.id).then((res) => active && setPricing(res))
    return () => {
      active = false
    }
  }, [apartment.id])

  const refresh = () => fetchApartmentEnrolments(apartment.id).then(setRows)

  const handleSubmit = async ({ parent, children, startDate }) => {
    setSubmitting(true)
    try {
      const link = await createApartmentPaymentLink({
        apartmentId: apartment.id,
        parent,
        children,
        startDate
      })
      // `whatsapp_sent` is the backend's real answer about the SEND. It is not
      // an answer about DELIVERY: these go out on a template Meta classifies as
      // Marketing, which it can withhold from the parent while still reporting
      // the send as accepted. So the link is now surfaced on screen every time,
      // not only when a failure is detected — the coordinator sends it manually
      // regardless.
      //
      // Compared against `false` explicitly, NOT for truthiness: Alfred deploys
      // on a push to main while oh-api prod is dispatch-only, so Alfred is
      // routinely ahead of the API. An older backend omits the field entirely,
      // and `undefined` read as "not sent" would put a red error on every
      // successful booking.
      if (link.whatsapp_sent !== false) {
        eventAlert(
          `Razorpay link for ${formatAmount(
            link.net
          )} created & sent on WhatsApp`,
          constants.SUCCESS
        )
      } else {
        eventAlert(
          `Razorpay link for ${formatAmount(link.net)} was created, but the ` +
            `WhatsApp did NOT go out. Copy the link below and send it yourself.`,
          constants.ERROR,
          'Link created — WhatsApp not sent'
        )
      }
      // Held in state rather than shown in the toast: a toast cannot be
      // selected, cannot be copied, and is gone in seconds. The modal stays
      // open on the link until the coordinator dismisses it.
      setCreatedLink({
        url: link.razorpay_short_url,
        amountLabel: formatAmount(link.net),
        whatsappSent: link.whatsapp_sent
      })
      refresh()
    } catch (error) {
      // request.js already surfaces a toast; keep the modal open to retry.
    } finally {
      setSubmitting(false)
    }
  }

  const openBooking = (parentId = '') => {
    setPrefillParentId(parentId)
    setCreatedLink(null)
    setIsModalOpen(true)
  }

  if (!rows) return <Loading />

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <ApartmentPageHeader
          title="Booking"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openBooking('')}
            >
              New Booking
            </Button>
          }
        />
      </div>

      <Table
        data={rows}
        columns={buildColumns(openBooking, rows)}
        options={tableOptions}
        disableFullHeight
      />

      <ModalForm
        isModalOpen={isModalOpen}
        onModalClose={() => {
          setIsModalOpen(false)
          setCreatedLink(null)
        }}
        modalTitle={
          createdLink
            ? `Booking created — ${apartment.name}`
            : `New Booking — ${apartment.name}`
        }
        enableSave={false}
        maxWidth="md"
      >
        {createdLink ? (
          <>
            <PaymentLinkPanel
              url={createdLink.url}
              amountLabel={createdLink.amountLabel}
              whatsappSent={createdLink.whatsappSent}
            />
            <Button
              style={{ marginTop: 16 }}
              onClick={() => {
                setIsModalOpen(false)
                setCreatedLink(null)
              }}
            >
              Done
            </Button>
          </>
        ) : options ? (
          <NewBookingForm
            // Remount per parent so a previous family's picks never linger.
            key={prefillParentId || 'new'}
            classes={classes}
            apartmentId={apartment.id}
            options={options}
            pricing={pricing}
            submitting={submitting}
            onSubmit={handleSubmit}
            initialParentId={prefillParentId}
          />
        ) : (
          <Loading />
        )}
      </ModalForm>
    </div>
  )
}

export default ApartmentEnrolments
